import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../../hooks/useApi';
import { buildBlogUrl } from '../../blog/blogApi';
import { formatBlogDate } from '../../blog/blogUtils';
import './AdminBlogEditor.css';
import { fmtDateTime } from '../../../utils/ruDates';

const PAGE_SIZE = 20;

const STATUS_LABELS = {
  draft: 'Черновик',
  published: 'Опубликовано',
  archived: 'Архив',
};


function AdminBlogList() {
  const { request, showStatus } = useApi();
  const navigate = useNavigate();

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [categoriesBusy, setCategoriesBusy] = useState(false);
  // Заметка сначала показывается человеку, публикуется только после подтверждения
  const [noteDraft, setNoteDraft] = useState(null);
  const [noteBusy, setNoteBusy] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalArticles, setTotalArticles] = useState(0);

  // Названия категорий приходят из настроек: в коде их больше нет
  const categoryLabels = useMemo(
    () => Object.fromEntries((categories || []).map((item) => [item.slug, item.name])),
    [categories]
  );

  const getCategoryLabel = useCallback(
    (category) => categoryLabels[category] || category || 'Без категории',
    [categoryLabels]
  );

  const categoryOptions = useMemo(() => {
    const known = (categories || []).map((item) => item.slug);
    const used = articles.map((article) => article.category).filter(Boolean);
    return Array.from(new Set([...known, ...used])).sort((left, right) =>
      (categoryLabels[left] || left).localeCompare(categoryLabels[right] || right, 'ru')
    );
  }, [articles, categories, categoryLabels]);

  async function loadArticles(nextPage = 1) {
    setLoading(true);

    try {
      const params = new URLSearchParams();
      if (searchTerm.trim()) params.set('search', searchTerm.trim());
      if (statusFilter) params.set('status', statusFilter);
      if (categoryFilter) params.set('category', categoryFilter);
      params.set('page', String(nextPage));
      params.set('per_page', String(PAGE_SIZE));

      const data = await request(`/api/v1/admin/blog/articles?${params.toString()}`);
      const total = data.total || 0;

      setArticles(data.articles || []);
      setTotalArticles(total);
      setTotalPages(Math.max(1, Math.ceil(total / PAGE_SIZE)));
      setPage(nextPage);
    } catch (error) {
      showStatus('error', error.message || 'Не удалось загрузить список статей');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCategories(false);
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      loadArticles(1);
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [searchTerm, statusFilter, categoryFilter]);

  async function handleDelete(id, title) {
    if (!window.confirm(`Удалить статью "${title}"?`)) {
      return;
    }

    try {
      await request(`/api/v1/admin/blog/articles/${id}`, { method: 'DELETE' });
      showStatus('success', 'Статья удалена');
      await loadArticles(page);
    } catch (error) {
      showStatus('error', error.message || 'Не удалось удалить статью');
    }
  }

  async function handlePublish(id) {
    try {
      await request(`/api/v1/admin/blog/articles/${id}/publish`, { method: 'POST' });
      showStatus('success', 'Статья опубликована');
      await loadArticles(page);
    } catch (error) {
      showStatus('error', error.message || 'Не удалось опубликовать статью');
    }
  }

  async function handleUnpublish(id) {
    try {
      await request(`/api/v1/admin/blog/articles/${id}/unpublish`, { method: 'POST' });
      showStatus('success', 'Публикация снята');
      await loadArticles(page);
    } catch (error) {
      showStatus('error', error.message || 'Не удалось снять публикацию');
    }
  }

  async function loadCategories(open = true) {
    try {
      const data = await request('/api/v1/admin/blog/categories');
      setCategories(data?.items || []);
      if (open) setCategoriesOpen(true);
    } catch (error) {
      showStatus('error', error.message || 'Не удалось загрузить категории');
    }
  }

  async function saveCategories() {
    setCategoriesBusy(true);
    try {
      const payload = categories
        .filter((item) => item.slug?.trim() && item.name?.trim())
        .map((item) => ({
          slug: item.slug.trim(),
          name: item.name.trim(),
          description: item.description || '',
        }));
      const data = await request('/api/v1/admin/blog/categories', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      setCategories(data?.items || []);
      showStatus('success', 'Категории сохранены');
      await loadArticles(page);
    } catch (error) {
      showStatus('error', error.message || 'Не удалось сохранить категории');
    } finally {
      setCategoriesBusy(false);
    }
  }

  function updateCategory(index, field, value) {
    setCategories((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  }

  // Готовый черновик открываем из статьи: повторная генерация стоит токенов
  function openSavedDraft(article) {
    setNoteDraft({ articleId: article.id, title: article.title, text: article.telegram_draft });
  }

  async function handleTelegramDraft(article, regenerate = false) {
    setNoteBusy(true);
    try {
      const data = await request(`/api/v1/admin/blog/articles/${article.id}/telegram-draft`, {
        method: 'POST',
      });
      setNoteDraft({ articleId: article.id, title: article.title, text: data?.text || '' });
      if (regenerate) showStatus('success', 'Заметка сгенерирована заново');
      await loadArticles(page);
    } catch (error) {
      showStatus('error', error.message || 'Не удалось подготовить заметку');
    } finally {
      setNoteBusy(false);
    }
  }

  // Правки в поле сохраняем в статью, чтобы они пережили обновление страницы
  async function saveDraftText() {
    if (!noteDraft) return;
    try {
      await request(`/api/v1/admin/blog/articles/${noteDraft.articleId}/telegram-draft`, {
        method: 'PUT',
        body: JSON.stringify({ text: noteDraft.text }),
      });
      showStatus('success', 'Черновик сохранён');
      await loadArticles(page);
    } catch (error) {
      showStatus('error', error.message || 'Не удалось сохранить черновик');
    }
  }

  async function handleTelegramPublish() {
    setNoteBusy(true);
    try {
      const data = await request(
        `/api/v1/admin/blog/articles/${noteDraft.articleId}/telegram-publish`,
        { method: 'POST', body: JSON.stringify({ text: noteDraft.text }) }
      );
      showStatus('success', `Опубликовано: ${data?.url || 'готово'}`);
      setNoteDraft(null);
      await loadArticles(page);
    } catch (error) {
      showStatus('error', error.message || 'Не удалось опубликовать заметку');
    } finally {
      setNoteBusy(false);
    }
  }

  // Эндпоинт принимает один markdown-файл с frontmatter, поэтому сначала выбор файла
  async function handleImportFile(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setImporting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const result = await request('/api/v1/admin/blog/articles/import', { method: 'POST', body: form });
      showStatus('success', `Импортировано: ${result?.title || file.name}`);
      await loadArticles(1);
    } catch (error) {
      showStatus('error', error.message || 'Не удалось импортировать markdown-статью');
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="admin-blog-list">
      <div className="admin-blog-list__header">
        <div>
          <h1 className="admin-blog-list__title">Blog CMS</h1>
          <p className="admin-blog-list__subtitle">
            {totalArticles} статей. Черновики, публикация и быстрые действия в одной панели.
          </p>
        </div>

        <div className="admin-blog-list__header-actions">
          <label className="admin-blog-button admin-blog-button--ghost" style={{ cursor: importing ? 'default' : 'pointer' }}>
            {importing ? 'Импорт...' : 'Импорт .md'}
            <input
              type="file"
              accept=".md,.markdown"
              onChange={handleImportFile}
              disabled={loading || importing}
              style={{ display: 'none' }}
            />
          </label>
          <button
            type="button"
            className="admin-blog-button admin-blog-button--ghost"
            onClick={() => (categoriesOpen ? setCategoriesOpen(false) : loadCategories())}
          >
            {categoriesOpen ? 'Скрыть категории' : 'Категории'}
          </button>
          <button type="button" className="admin-blog-button admin-blog-button--primary" onClick={() => navigate('/admin/blog/new')}>
            Новая статья
          </button>
        </div>
      </div>

      <div className="admin-blog-list__controls">
        <input
          type="search"
          className="blog-meta-field__input admin-blog-list__search"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Поиск по title, description и content"
        />

        <select className="blog-meta-field__input admin-blog-list__select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option value="">Все статусы</option>
          <option value="draft">Черновик</option>
          <option value="published">Опубликовано</option>
          <option value="archived">Архив</option>
        </select>

        <select className="blog-meta-field__input admin-blog-list__select" value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
          <option value="">Все категории</option>
          {categoryOptions.map((category) => <option key={category} value={category}>{getCategoryLabel(category)}</option>)}
        </select>
      </div>

      {loading ? <div className="admin-blog-list__state">Загрузка статей...</div> : null}
      {!loading && !articles.length ? <div className="admin-blog-list__state admin-blog-list__state--empty">По текущим фильтрам статей нет.</div> : null}

      {noteDraft ? (
        <div className="admin-blog-categories">
          <p className="admin-blog-list__subtitle">
            Заметка для «{noteDraft.title}». Черновик хранится в статье: страницу можно
            закрыть и вернуться. После публикации текст правится только в самом Телеграме.
          </p>
          <textarea
            rows={12}
            style={{ width: '100%', padding: 12 }}
            value={noteDraft.text}
            onChange={(event) => setNoteDraft({ ...noteDraft, text: event.target.value })}
          />
          <p className="admin-blog-list__subtitle">{noteDraft.text.length} из 4096 символов</p>
          <div className="admin-blog-categories__actions">
            <button type="button" onClick={() => setNoteDraft(null)}>Закрыть</button>
            <button type="button" onClick={saveDraftText}>Сохранить черновик</button>
            <button
              type="button"
              disabled={noteBusy}
              onClick={() =>
                handleTelegramDraft(
                  { id: noteDraft.articleId, title: noteDraft.title },
                  true
                )
              }
            >
              Сгенерировать заново
            </button>
            <button
              type="button"
              className="admin-blog-button admin-blog-button--primary"
              disabled={noteBusy}
              onClick={handleTelegramPublish}
            >
              Опубликовать в канал
            </button>
          </div>
        </div>
      ) : null}

      {categoriesOpen ? (
        <div className="admin-blog-categories">
          <p className="admin-blog-list__subtitle">
            Категории берутся отсюда, а не из кода. Slug участвует в адресе раздела блога,
            название видно читателю.
          </p>
          {categories.map((item, index) => (
            <div className="admin-blog-categories__row" key={index}>
              <input
                value={item.slug || ''}
                onChange={(event) => updateCategory(index, 'slug', event.target.value)}
                placeholder="slug"
              />
              <input
                value={item.name || ''}
                onChange={(event) => updateCategory(index, 'name', event.target.value)}
                placeholder="Название"
              />
              <input
                value={item.description || ''}
                onChange={(event) => updateCategory(index, 'description', event.target.value)}
                placeholder="Описание (необязательно)"
              />
              <button
                type="button"
                onClick={() => setCategories((prev) => prev.filter((_, i) => i !== index))}
              >
                Удалить
              </button>
            </div>
          ))}
          <div className="admin-blog-categories__actions">
            <button
              type="button"
              onClick={() => setCategories((prev) => [...prev, { slug: '', name: '', description: '' }])}
            >
              Добавить категорию
            </button>
            <button
              type="button"
              className="admin-blog-button admin-blog-button--primary"
              disabled={categoriesBusy}
              onClick={saveCategories}
            >
              Сохранить категории
            </button>
          </div>
        </div>
      ) : null}

      {!loading && articles.length ? (
        <>
          <div className="admin-blog-list__rows">
            {articles.map((article) => (
              <article key={article.id} className="admin-blog-row">
                <div className="admin-blog-row__main">
                  <h3 className="admin-blog-row__title">{article.title}</h3>
                  <p className="admin-blog-row__meta">
                    <span className={`blog-status-badge blog-status-badge--${article.status}`}>{STATUS_LABELS[article.status] || article.status}</span>
                    {article.telegram_url ? (
                      <span className="blog-status-badge blog-status-badge--telegram" title="Заметка опубликована в канале">
                        ✓ Телеграм
                      </span>
                    ) : article.scheduled_publish_at ? (
                      <span
                        className="blog-status-badge blog-status-badge--draft"
                        title="Статья выйдет автоматически"
                      >
                        Выйдет {fmtDateTime(article.scheduled_publish_at)}
                      </span>
                    ) : article.telegram_draft ? (
                      <span className="blog-status-badge blog-status-badge--draft" title="Заметка подготовлена, но не опубликована">
                        Заметка готова
                      </span>
                    ) : null}
                    <span>{getCategoryLabel(article.category)}</span>
                    <span>{article.reading_time || '—'} мин</span>
                    <span>{formatBlogDate(article.published_at || article.created_at)}</span>
                  </p>
                </div>

                <div className="admin-blog-row__actions">
                  <button type="button" className="admin-blog-row__action" onClick={() => navigate(`/admin/blog/${article.id}/edit`)}>
                    Редактировать
                  </button>

                  {article.telegram_url ? (
                    <a
                      className="admin-blog-row__action"
                      href={article.telegram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Пост в Телеграме ↗
                    </a>
                  ) : article.telegram_draft ? (
                    <button
                      type="button"
                      className="admin-blog-row__action"
                      onClick={() => openSavedDraft(article)}
                    >
                      Открыть черновик
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="admin-blog-row__action"
                      disabled={noteBusy}
                      onClick={() => handleTelegramDraft(article)}
                    >
                      Заметка для Телеграма
                    </button>
                  )}

                  {article.status === 'draft' ? (
                    <button type="button" className="admin-blog-row__action admin-blog-row__action--success" onClick={() => handlePublish(article.id)}>
                      Опубликовать
                    </button>
                  ) : null}

                  {article.status === 'published' ? (
                    <>
                      <a className="admin-blog-row__action" href={buildBlogUrl(`/blog/${article.slug}`)} target="_blank" rel="noreferrer">
                        Открыть
                      </a>
                      <button type="button" className="admin-blog-row__action" onClick={() => handleUnpublish(article.id)}>
                        Снять
                      </button>
                    </>
                  ) : null}

                  <button type="button" className="admin-blog-row__action admin-blog-row__action--danger" onClick={() => handleDelete(article.id, article.title)}>
                    Удалить
                  </button>
                </div>
              </article>
            ))}
          </div>

          {totalPages > 1 ? (
            <div className="admin-blog-pagination">
              <button type="button" className="admin-blog-button admin-blog-button--ghost" disabled={page <= 1} onClick={() => loadArticles(page - 1)}>
                Назад
              </button>
              <span className="admin-blog-pagination__status">Страница {page} из {totalPages}</span>
              <button type="button" className="admin-blog-button admin-blog-button--ghost" disabled={page >= totalPages} onClick={() => loadArticles(page + 1)}>
                Далее
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

export default AdminBlogList;
